import { FormEvent, useEffect, useMemo, useState } from 'react';
import { ExternalLink, Phone, Sparkles } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { PromoteAccountDialog } from '@components/accounts/PromoteAccountDialog';
import { AIPanel } from '@components/common/AIPanel';
import { EmptyState } from '@components/common/EmptyState';
import { SearchableSelect } from '@components/common/SearchableSelect';
import { SectionCard } from '@components/common/SectionCard';
import {
  useAddLeadNote,
  useApolloEmailAccounts,
  useApolloSequences,
  useApolloUsers,
  useCRMSettings,
  useConvertLead,
  useCallSession,
  useCalls,
  useCreateCall,
  useCreateLead,
  useEnrollLeadInApolloSequence,
  useLead,
  useLeadTimeline,
  useQualifyLead,
  useStopLeadInApolloSequence,
  useSyncLeadToApollo,
  useUpdateLead,
  useUsers,
} from '@hooks/useCRM';
import { useCallProvider } from '@providers/CallProvider';
import { getApolloLastEventForLead, getApolloLeadUrls } from '@lib/apollo';
import { formatDate, formatRelativeDate } from '@lib/format';
import type { CRMApolloEnrollRequest, CRMCallCreatePayload, CRMLeadPayload } from '@/types/api';

const EMPTY_LEAD_FORM: CRMLeadPayload = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company_name: '',
  job_title: '',
  city: '',
  priority: 'medium',
  source_type: 'manual',
  status: 'new',
  qualification_notes: '',
  why_target: '',
  follow_up_at: '',
  linkedin_url: '',
  owner_user_id: null,
};

const LEAD_STATUS_OPTIONS = [
  { id: 'new', label: 'New' },
  { id: 'qualified', label: 'Qualified' },
  { id: 'called', label: 'Called' },
  { id: 'interested', label: 'Interested' },
  { id: 'demo_scheduled', label: 'Demo scheduled' },
  { id: 'converted', label: 'Converted' },
  { id: 'disqualified', label: 'Disqualified' },
];

const PRIORITY_OPTIONS = [
  { id: 'high', label: 'High' },
  { id: 'medium', label: 'Medium' },
  { id: 'low', label: 'Low' },
];

const APOLLO_SEQUENCE_STATUS_OPTIONS = [
  { id: 'active', label: 'Active' },
  { id: 'paused', label: 'Paused' },
  { id: 'finished', label: 'Finished' },
];

const CALL_STATUS_OPTIONS = [
  { id: 'queued', label: 'Queued' },
  { id: 'dialing', label: 'Dialing' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'completed', label: 'Completed' },
  { id: 'no_answer', label: 'No answer' },
  { id: 'busy', label: 'Busy' },
  { id: 'failed', label: 'Failed' },
];

const RECORDING_OPTIONS = [
  { id: 'yes', label: 'Requested' },
  { id: 'no', label: 'Not requested' },
];

type ApolloActionFormState = {
  sequence_id: string;
  email_account_id: string;
  user_id: string;
  status: string;
};

const EMPTY_APOLLO_ACTION_FORM: ApolloActionFormState = {
  sequence_id: '',
  email_account_id: '',
  user_id: '',
  status: 'active',
};

function mapLeadToForm(lead: CRMLeadPayload) {
  return {
    first_name: lead.first_name ?? '',
    last_name: lead.last_name ?? '',
    email: lead.email ?? '',
    phone: lead.phone ?? '',
    company_name: lead.company_name ?? '',
    job_title: lead.job_title ?? '',
    city: lead.city ?? '',
    priority: lead.priority ?? 'medium',
    source_type: lead.source_type ?? 'manual',
    status: lead.status ?? 'new',
    qualification_notes: lead.qualification_notes ?? '',
    why_target: lead.why_target ?? '',
    follow_up_at: formatDateTimeLocalInput(lead.follow_up_at),
    linkedin_url: lead.linkedin_url ?? '',
    owner_user_id: lead.owner_user_id ?? null,
  };
}

function normalizeOptional(value: string) {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function formatDateTimeLocalInput(value?: string | null) {
  if (!value) {
    return '';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  const hours = String(parsed.getHours()).padStart(2, '0');
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

type LeadDetailPageProps = {
  isCreating?: boolean;
};

export function LeadDetailPage({ isCreating = false }: LeadDetailPageProps) {
  const navigate = useNavigate();
  const { leadId } = useParams();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [noteBody, setNoteBody] = useState('');
  const [formState, setFormState] = useState<CRMLeadPayload>(EMPTY_LEAD_FORM);
  const [convertAccountName, setConvertAccountName] = useState('');
  const [createOpportunity, setCreateOpportunity] = useState(true);
  const [opportunityName, setOpportunityName] = useState('');
  const [apolloForm, setApolloForm] = useState<ApolloActionFormState>(EMPTY_APOLLO_ACTION_FORM);
  const [apolloMessage, setApolloMessage] = useState<string | null>(null);
  const [apolloError, setApolloError] = useState<string | null>(null);
  const [callForm, setCallForm] = useState<CRMCallCreatePayload>({
    to_number: '',
    status: 'completed',
    recording_requested: true,
    summary: '',
    body: '',
    duration_seconds: null,
  });
  const { data: lead, isLoading } = useLead(leadId);
  const { data: settings } = useCRMSettings();
  const { data: timeline } = useLeadTimeline(leadId);
  const { data: callSession } = useCallSession();
  const { data: calls } = useCalls({ leadId });
  const apolloReady = Boolean(settings?.apollo.enabled && settings?.apollo.has_api_key);
  const { data: apolloSequences } = useApolloSequences(undefined, apolloReady);
  const { data: apolloEmailAccounts } = useApolloEmailAccounts(apolloReady);
  const { data: apolloUsers } = useApolloUsers(apolloReady);
  const createLeadMutation = useCreateLead();
  const createCallMutation = useCreateCall();
  const updateLeadMutation = useUpdateLead();
  const qualifyLeadMutation = useQualifyLead();
  const convertLeadMutation = useConvertLead();
  const addNoteMutation = useAddLeadNote();
  const syncLeadToApolloMutation = useSyncLeadToApollo();
  const enrollLeadInApolloSequenceMutation = useEnrollLeadInApolloSequence();
  const stopLeadInApolloSequenceMutation = useStopLeadInApolloSequence();
  const { data: users } = useUsers();
  const { activeCall, isStarting, startLiveCall } = useCallProvider();
  const ownerOptions = [
    { id: '', label: 'Unassigned' },
    ...(users ?? []).map((user) => ({
      id: user.id,
      label: `${user.first_name} ${user.last_name}`.trim() || user.email,
      description: user.email,
    })),
  ];

  useEffect(() => {
    if (lead) {
      setSubmitError(null);
      setFormState(mapLeadToForm(lead));
      setConvertAccountName(lead.company_name ?? '');
      setOpportunityName(lead.company_name ? `${lead.company_name} - Pilot` : '');
      setApolloError(null);
      setCallForm((current) => ({
        ...current,
        to_number: lead.phone ?? '',
        lead_id: lead.id,
        account_id: lead.account_id ?? null,
        contact_id: lead.contact_id ?? null,
        owner_user_id: lead.owner_user_id ?? null,
      }));
      return;
    }

    if (isCreating) {
      setSubmitError(null);
      setFormState(EMPTY_LEAD_FORM);
      setConvertAccountName('');
      setOpportunityName('');
      setApolloForm(EMPTY_APOLLO_ACTION_FORM);
      setApolloMessage(null);
      setApolloError(null);
      setCallForm({
        to_number: '',
        status: 'completed',
        recording_requested: true,
        summary: '',
        body: '',
        duration_seconds: null,
      });
    }
  }, [isCreating, lead]);

  useEffect(() => {
    setApolloForm({
      sequence_id: lead?.apollo_sequence_id ?? settings?.apollo.default_sequence_id ?? '',
      email_account_id: settings?.apollo.default_email_account_id ?? '',
      user_id: settings?.apollo.default_user_id ?? '',
      status: lead?.apollo_sequence_status && lead.apollo_sequence_status !== 'removed' ? lead.apollo_sequence_status : 'active',
    });
  }, [
    lead?.apollo_sequence_id,
    lead?.apollo_sequence_status,
    settings?.apollo.default_email_account_id,
    settings?.apollo.default_sequence_id,
    settings?.apollo.default_user_id,
  ]);

  const apolloSequenceOptions = useMemo(() => {
    const items = apolloSequences ? [...apolloSequences] : [];
    if (apolloForm.sequence_id && !items.some((sequence) => sequence.id === apolloForm.sequence_id)) {
      items.unshift({
        id: apolloForm.sequence_id,
        name: apolloForm.sequence_id,
      });
    }
    return items;
  }, [apolloForm.sequence_id, apolloSequences]);

  const apolloEmailAccountOptions = useMemo(() => {
    const items = apolloEmailAccounts ? [...apolloEmailAccounts] : [];
    if (
      apolloForm.email_account_id &&
      !items.some((emailAccount) => emailAccount.id === apolloForm.email_account_id)
    ) {
      items.unshift({
        id: apolloForm.email_account_id,
        email: apolloForm.email_account_id,
      });
    }
    return items;
  }, [apolloEmailAccounts, apolloForm.email_account_id]);

  const apolloUserOptions = useMemo(() => {
    const items = apolloUsers ? [...apolloUsers] : [];
    if (apolloForm.user_id && !items.some((user) => user.id === apolloForm.user_id)) {
      items.unshift({
        id: apolloForm.user_id,
        name: apolloForm.user_id,
      });
    }
    return items;
  }, [apolloForm.user_id, apolloUsers]);

  const apolloLinks = useMemo(() => (lead ? getApolloLeadUrls(lead) : null), [lead]);
  const lastApolloEvent = useMemo(() => (lead ? getApolloLastEventForLead(lead) : null), [lead]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);

    const payload: CRMLeadPayload = {
      first_name: normalizeOptional(formState.first_name ?? ''),
      last_name: normalizeOptional(formState.last_name ?? ''),
      email: normalizeOptional(formState.email ?? ''),
      phone: normalizeOptional(formState.phone ?? ''),
      company_name: normalizeOptional(formState.company_name ?? ''),
      job_title: normalizeOptional(formState.job_title ?? ''),
      city: normalizeOptional(formState.city ?? ''),
      priority: formState.priority ?? 'medium',
      source_type: formState.source_type ?? 'manual',
      status: formState.status ?? 'new',
      qualification_notes: normalizeOptional(formState.qualification_notes ?? ''),
      why_target: normalizeOptional(formState.why_target ?? ''),
      follow_up_at: normalizeOptional(formState.follow_up_at ?? ''),
      linkedin_url: normalizeOptional(formState.linkedin_url ?? ''),
      owner_user_id: formState.owner_user_id ?? null,
    };

    try {
      if (leadId) {
        await updateLeadMutation.mutateAsync({ leadId, payload });
        return;
      }

      const createdLead = await createLeadMutation.mutateAsync(payload);
      navigate(`/leads/${createdLead.id}`);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to save lead.');
    }
  }

  async function handleQualifyLead() {
    if (!leadId) {
      return;
    }
    await qualifyLeadMutation.mutateAsync({
      leadId,
      payload: {
        status: formState.status || 'qualified',
        qualification_notes: normalizeOptional(formState.qualification_notes ?? ''),
      },
    });
  }

  async function handleConvertLead() {
    if (!leadId) {
      return;
    }
    const response = await convertLeadMutation.mutateAsync({
      leadId,
      payload: {
        account_name: normalizeOptional(convertAccountName),
        create_opportunity: createOpportunity,
        opportunity_name: createOpportunity ? normalizeOptional(opportunityName) : null,
        opportunity_stage: 'discovery',
      },
    });
    if (response.account_id) {
      navigate(`/accounts/${response.account_id}`);
    }
  }

  async function handleAddNote(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!leadId || !noteBody.trim()) {
      return;
    }
    await addNoteMutation.mutateAsync({
      leadId,
      payload: { summary: 'Lead note', body: noteBody.trim() },
    });
    setNoteBody('');
  }

  async function handleCreateCall(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!leadId || !callForm.to_number?.trim()) {
      return;
    }
    await createCallMutation.mutateAsync({
      ...callForm,
      lead_id: leadId,
      account_id: lead?.account_id ?? null,
      contact_id: lead?.contact_id ?? null,
      owner_user_id: lead?.owner_user_id ?? null,
      to_number: callForm.to_number.trim(),
      duration_seconds: callForm.duration_seconds ? Number(callForm.duration_seconds) : null,
      summary: callForm.summary?.trim() || null,
      body: callForm.body?.trim() || null,
    });
    setCallForm((current) => ({
      ...current,
      status: 'completed',
      summary: '',
      body: '',
      duration_seconds: null,
    }));
  }

  async function handleStartLiveCall() {
    if (!leadId || !callForm.to_number?.trim()) {
      return;
    }
    await startLiveCall({
      to_number: callForm.to_number.trim(),
      lead_id: leadId,
      account_id: lead?.account_id ?? null,
      contact_id: lead?.contact_id ?? null,
      owner_user_id: lead?.owner_user_id ?? null,
      recording_requested: callForm.recording_requested ?? true,
      consent_status: callForm.consent_status ?? 'unknown',
    });
  }

  async function handleApolloSync() {
    if (!leadId) {
      return;
    }

    setApolloMessage(null);
    setApolloError(null);

    try {
      const response = await syncLeadToApolloMutation.mutateAsync(leadId);
      setApolloMessage(`Synced to Apollo at ${formatDate(response.synced_at)}.`);
    } catch (error) {
      setApolloError(error instanceof Error ? error.message : 'Failed to sync lead to Apollo.');
    }
  }

  async function handleApolloEnroll() {
    if (!leadId) {
      return;
    }

    setApolloMessage(null);
    setApolloError(null);

    const payload: CRMApolloEnrollRequest = {
      sequence_id: normalizeOptional(apolloForm.sequence_id),
      email_account_id: normalizeOptional(apolloForm.email_account_id),
      user_id: normalizeOptional(apolloForm.user_id),
      status: normalizeOptional(apolloForm.status) ?? 'active',
    };

    try {
      const response = await enrollLeadInApolloSequenceMutation.mutateAsync({ leadId, payload });
      setApolloMessage(`Enrolled in Apollo sequence ${response.sequence_id}.`);
    } catch (error) {
      setApolloError(error instanceof Error ? error.message : 'Failed to enroll lead in Apollo sequence.');
    }
  }

  async function handleApolloStop() {
    if (!leadId) {
      return;
    }

    setApolloMessage(null);
    setApolloError(null);

    try {
      const response = await stopLeadInApolloSequenceMutation.mutateAsync(leadId);
      setApolloMessage(`Removed from Apollo sequence ${response.sequence_id}.`);
    } catch (error) {
      setApolloError(error instanceof Error ? error.message : 'Failed to remove lead from Apollo sequence.');
    }
  }

  const isSaving = createLeadMutation.isPending || updateLeadMutation.isPending;

  if (!isCreating && !leadId) {
    return <EmptyState title="Lead not found" body="Select a lead from the list to view its profile." />;
  }

  return (
    <div className="detail-stack">
      <section className="entity-header">
        <div className="entity-header-copy">
          <p className="entity-breadcrumb">Leads / {leadId ? 'Detail' : 'New'}</p>
          <h1>
            {[lead?.first_name, lead?.last_name].filter(Boolean).join(' ') || lead?.company_name || (leadId ? 'Lead Detail' : 'Create Lead')}
          </h1>
          <p>
            {lead
              ? `${lead.company_name || 'No company yet'} from ${lead.source_type} with ${lead.status} status and ${lead.priority} priority.`
              : 'Create a lead here, then qualify it, log calls, and convert it from its dedicated record page.'}
          </p>
        </div>
        <div className="entity-header-actions">
          <button type="button" className="button button-secondary" onClick={() => navigate('/leads')}>Back to Leads</button>
        </div>
      </section>

      {lead ? (
        <section className="entity-meta-grid">
          <article className="entity-meta-card">
            <span>Status</span>
            <strong>{lead.status}</strong>
          </article>
          <article className="entity-meta-card">
            <span>Priority</span>
            <strong>{lead.priority}</strong>
          </article>
          <article className="entity-meta-card">
            <span>Source</span>
            <strong>{lead.source_type}</strong>
          </article>
          <article className="entity-meta-card">
            <span>Follow-up</span>
            <strong>{formatDate(lead.follow_up_at) || 'Not scheduled'}</strong>
          </article>
        </section>
      ) : null}

      <SectionCard
        title={leadId ? 'Lead Detail' : 'New Lead'}
        subtitle="Capture qualification context, founder notes, and conversion steps."
      >
        {isLoading ? (
          <div className="loading-row">Loading lead detail…</div>
        ) : !isCreating && !lead ? (
          <EmptyState title="Lead not found" body="This lead record could not be loaded." />
        ) : (
          <form className="detail-form" onSubmit={handleSubmit}>
            <div className="form-grid">
              <label className="form-field">
                <span>First Name</span>
                <input value={formState.first_name ?? ''} onChange={(event) => setFormState((current) => ({ ...current, first_name: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Last Name</span>
                <input value={formState.last_name ?? ''} onChange={(event) => setFormState((current) => ({ ...current, last_name: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Email</span>
                <input type="email" value={formState.email ?? ''} onChange={(event) => setFormState((current) => ({ ...current, email: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Phone</span>
                <input value={formState.phone ?? ''} onChange={(event) => setFormState((current) => ({ ...current, phone: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Company</span>
                <input value={formState.company_name ?? ''} onChange={(event) => setFormState((current) => ({ ...current, company_name: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Job Title</span>
                <input value={formState.job_title ?? ''} onChange={(event) => setFormState((current) => ({ ...current, job_title: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>City</span>
                <input value={formState.city ?? ''} onChange={(event) => setFormState((current) => ({ ...current, city: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>LinkedIn</span>
                <input value={formState.linkedin_url ?? ''} onChange={(event) => setFormState((current) => ({ ...current, linkedin_url: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Status</span>
                <SearchableSelect
                  options={LEAD_STATUS_OPTIONS}
                  value={formState.status ?? 'new'}
                  onChange={(value) => setFormState((current) => ({ ...current, status: value }))}
                  placeholder="Select status"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Priority</span>
                <SearchableSelect
                  options={PRIORITY_OPTIONS}
                  value={formState.priority ?? 'medium'}
                  onChange={(value) => setFormState((current) => ({ ...current, priority: value }))}
                  placeholder="Select priority"
                  clearable={false}
                />
              </label>
              <label className="form-field">
                <span>Source</span>
                <input value={formState.source_type ?? 'manual'} onChange={(event) => setFormState((current) => ({ ...current, source_type: event.target.value }))} />
              </label>
              <label className="form-field">
                <span>Follow-up</span>
                <input
                  type="datetime-local"
                  value={formState.follow_up_at ?? ''}
                  onChange={(event) => setFormState((current) => ({ ...current, follow_up_at: event.target.value }))}
                />
              </label>
              <label className="form-field">
                <span>Owner</span>
                <SearchableSelect
                  options={ownerOptions}
                  value={formState.owner_user_id ?? ''}
                  onChange={(value) => setFormState((current) => ({ ...current, owner_user_id: value || null }))}
                  placeholder="Unassigned"
                  clearable={false}
                />
              </label>
            </div>

            <label className="form-field">
              <span>Why target</span>
              <textarea rows={3} value={formState.why_target ?? ''} onChange={(event) => setFormState((current) => ({ ...current, why_target: event.target.value }))} />
            </label>

            <label className="form-field">
              <span>Qualification notes</span>
              <textarea
                rows={4}
                value={formState.qualification_notes ?? ''}
                onChange={(event) => setFormState((current) => ({ ...current, qualification_notes: event.target.value }))}
              />
            </label>

            <div className="form-actions">
              <button type="submit" className="button button-primary" disabled={isSaving}>
                {isSaving ? 'Saving…' : leadId ? 'Save Lead' : 'Create Lead'}
              </button>
              {leadId ? (
                <button type="button" className="button button-secondary" onClick={handleQualifyLead} disabled={qualifyLeadMutation.isPending}>
                  {qualifyLeadMutation.isPending ? 'Updating…' : 'Update Qualification'}
                </button>
              ) : null}
            </div>
            {submitError ? <p className="form-error">{submitError}</p> : null}
          </form>
        )}
      </SectionCard>

      {leadId && lead ? (
        <>
          <SectionCard
            title="Customer Promotion"
            subtitle="Create the AP tenant and invite the first admin directly from this lead."
          >
            <div className="detail-stack">
              <p className="muted-copy">
                {lead.account_id
                  ? 'This lead is already tied to an account; promotion will use the linked account context.'
                  : 'Promotion will create a customer account first, then provision the AP tenant.'}
              </p>
              <div className="entity-header-actions">
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => setPromoteOpen(true)}
                >
                  Promote to customer
                </button>
              </div>
            </div>
          </SectionCard>

          <PromoteAccountDialog
            accountId={lead.id}
            accountName={lead.company_name || [lead.first_name, lead.last_name].filter(Boolean).join(' ') || 'New customer'}
            primaryContact={{
              email: lead.email,
              first_name: lead.first_name,
              last_name: lead.last_name,
            }}
            open={promoteOpen}
            onClose={() => setPromoteOpen(false)}
            targetType="lead"
          />

          <SectionCard title="Apollo" subtitle="Internal outbound bridge: sync the lead into Apollo, enroll it into a sequence, and keep the CRM as the control surface.">
            <div className="detail-stack">
              <div className="call-session-banner">
                <div>
                  <strong>{apolloReady ? 'Apollo integration ready' : 'Apollo integration not configured'}</strong>
                  <p>
                    {apolloReady
                      ? `Last synced ${formatDate(lead.apollo_last_synced_at) || 'never'}. Use CRM-owned actions here to push the lead into Apollo.`
                      : 'Set the Apollo API key and defaults in Settings before using sync or sequence actions.'}
                  </p>
                </div>
                <span className={`pill ${lead.apollo_sequence_status && lead.apollo_sequence_status !== 'removed' ? 'pill-medium' : 'pill-soft'}`}>
                  {lead.apollo_sequence_status ?? 'not enrolled'}
                </span>
              </div>

              <div className="integration-meta-grid">
                <div>
                  <dt>Current Sequence</dt>
                  <dd>{lead.apollo_sequence_id ?? 'None'}</dd>
                </div>
                <div>
                  <dt>Last Sync</dt>
                  <dd>{formatDate(lead.apollo_last_synced_at) || 'Never'}</dd>
                </div>
                <div>
                  <dt>Sequence Status</dt>
                  <dd>{lead.apollo_sequence_status ?? 'Not enrolled'}</dd>
                </div>
                <div>
                  <dt>Apollo Contact</dt>
                  <dd>{apolloLinks?.contactId ?? 'Not synced'}</dd>
                </div>
                <div>
                  <dt>Apollo Account</dt>
                  <dd>{apolloLinks?.accountId ?? 'Not synced'}</dd>
                </div>
              </div>

              <div className="apollo-inspector-actions">
                {apolloLinks?.contactUrl ? (
                  <button type="button" className="button button-secondary" onClick={() => window.open(apolloLinks.contactUrl!, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink size={16} />
                    Open Contact in Apollo
                  </button>
                ) : null}
                {apolloLinks?.accountUrl ? (
                  <button type="button" className="button button-secondary" onClick={() => window.open(apolloLinks.accountUrl!, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink size={16} />
                    Open Account in Apollo
                  </button>
                ) : null}
                {apolloLinks?.sequenceUrl ? (
                  <button type="button" className="button button-secondary" onClick={() => window.open(apolloLinks.sequenceUrl!, '_blank', 'noopener,noreferrer')}>
                    <ExternalLink size={16} />
                    Open Sequence in Apollo
                  </button>
                ) : null}
              </div>

              <div className="apollo-inspector-section">
                <h4>Apollo Activity</h4>
                {lastApolloEvent ? (
                  <dl className="apollo-inspector-meta">
                    <div>
                      <dt>Last Event</dt>
                      <dd>{lastApolloEvent.event_type ?? 'Unknown'}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>{lastApolloEvent.status ?? lead.apollo_sequence_status ?? 'Unknown'}</dd>
                    </div>
                    <div>
                      <dt>Occurred</dt>
                      <dd>{formatDate(lastApolloEvent.occurred_at) || 'Unknown'}</dd>
                    </div>
                    <div>
                      <dt>Relative</dt>
                      <dd>{formatRelativeDate(lastApolloEvent.occurred_at)}</dd>
                    </div>
                    <div>
                      <dt>Reason</dt>
                      <dd>{lastApolloEvent.reason ?? '—'}</dd>
                    </div>
                    <div>
                      <dt>Note</dt>
                      <dd>{lastApolloEvent.note ?? '—'}</dd>
                    </div>
                  </dl>
                ) : (
                  <div className="empty-state">
                    <h3>No Apollo event mirrored yet</h3>
                    <p>Once Apollo sends sequence lifecycle events back into CRM, the latest Apollo activity will show here.</p>
                  </div>
                )}
              </div>

              <div className="form-grid">
                <label className="form-field">
                  <span>Apollo Sequence</span>
                  <SearchableSelect
                    options={[
                      {
                        id: '',
                        label: apolloReady ? 'Use CRM default Apollo sequence' : 'Configure Apollo in Settings first',
                      },
                      ...apolloSequenceOptions.map((sequence) => ({
                        id: sequence.id,
                        label: sequence.name,
                        description: [sequence.status, sequence.owner_name].filter(Boolean).join(' • '),
                      })),
                    ]}
                    value={apolloForm.sequence_id}
                    onChange={(value) => setApolloForm((current) => ({ ...current, sequence_id: value }))}
                    disabled={!apolloReady}
                    placeholder="Apollo sequence"
                    clearable={false}
                  />
                </label>

                <label className="form-field">
                  <span>Email Account</span>
                  <SearchableSelect
                    options={[
                      {
                        id: '',
                        label: apolloReady ? 'Use CRM default Apollo email account' : 'Configure Apollo in Settings first',
                      },
                      ...apolloEmailAccountOptions.map((account) => ({
                        id: account.id,
                        label: account.email,
                        description: [account.sender_name, account.status].filter(Boolean).join(' • '),
                      })),
                    ]}
                    value={apolloForm.email_account_id}
                    onChange={(value) => setApolloForm((current) => ({ ...current, email_account_id: value }))}
                    disabled={!apolloReady}
                    placeholder="Apollo email account"
                    clearable={false}
                  />
                </label>

                <label className="form-field">
                  <span>Apollo User</span>
                  <SearchableSelect
                    options={[
                      {
                        id: '',
                        label: apolloReady ? 'Use CRM default Apollo user' : 'Configure Apollo in Settings first',
                      },
                      ...apolloUserOptions.map((user) => ({
                        id: user.id,
                        label: user.name,
                        description: user.email ?? undefined,
                      })),
                    ]}
                    value={apolloForm.user_id}
                    onChange={(value) => setApolloForm((current) => ({ ...current, user_id: value }))}
                    disabled={!apolloReady}
                    placeholder="Apollo user"
                    clearable={false}
                  />
                </label>

                <label className="form-field">
                  <span>Sequence Status</span>
                  <SearchableSelect
                    options={APOLLO_SEQUENCE_STATUS_OPTIONS}
                    value={apolloForm.status}
                    onChange={(value) => setApolloForm((current) => ({ ...current, status: value }))}
                    disabled={!apolloReady}
                    placeholder="Sequence status"
                    clearable={false}
                  />
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="button button-secondary" onClick={() => void handleApolloSync()} disabled={!apolloReady || syncLeadToApolloMutation.isPending}>
                  {syncLeadToApolloMutation.isPending ? 'Syncing…' : 'Sync to Apollo'}
                </button>
                <button
                  type="button"
                  className="button button-primary"
                  onClick={() => void handleApolloEnroll()}
                  disabled={!apolloReady || enrollLeadInApolloSequenceMutation.isPending}
                >
                  {enrollLeadInApolloSequenceMutation.isPending ? 'Enrolling…' : 'Enroll in Sequence'}
                </button>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => void handleApolloStop()}
                  disabled={!apolloReady || stopLeadInApolloSequenceMutation.isPending || !lead.apollo_sequence_id}
                >
                  {stopLeadInApolloSequenceMutation.isPending ? 'Removing…' : 'Remove from Sequence'}
                </button>
              </div>

              {apolloMessage ? <p className="success-copy">{apolloMessage}</p> : null}
              {apolloError ? <p className="form-error">{apolloError}</p> : null}
            </div>
          </SectionCard>

          <SectionCard title="Calls" subtitle="Dialer foundation for outbound calls and transcript processing.">
            <div className="detail-stack">
              <div className="call-session-banner">
                <div>
                  <strong>{callSession?.available ? 'Dialer config ready' : 'Dialer setup incomplete'}</strong>
                  <p>
                    {callSession?.available
                      ? `LiveKit session metadata is available for caller ID ${callSession.outbound_caller_id ?? 'not set'}.`
                      : `Missing: ${callSession?.missing_configuration.join(', ') || 'LIVEKIT_WS_URL, LIVEKIT_API_KEY, LIVEKIT_API_SECRET, CRM_OUTBOUND_CALLER_ID'}`}
                  </p>
                </div>
                <span className={`pill ${callSession?.available ? 'pill-medium' : 'pill-low'}`}>
                  {callSession?.provider ?? 'livekit'}
                </span>
              </div>

              <form className="detail-subsection call-log-panel" onSubmit={handleCreateCall}>
                <div className="subsection-header">
                  <h3>Log Call</h3>
                  <p>Create the call record now; LiveKit media wiring can attach to this contract next.</p>
                </div>
                <div className="form-grid">
                  <label className="form-field">
                    <span>To Number</span>
                    <input
                      value={callForm.to_number ?? ''}
                      onChange={(event) => setCallForm((current) => ({ ...current, to_number: event.target.value }))}
                      placeholder="+46 70 000 00 00"
                    />
                  </label>
                  <label className="form-field">
                    <span>Status</span>
                    <SearchableSelect
                      options={CALL_STATUS_OPTIONS}
                      value={callForm.status ?? 'completed'}
                      onChange={(value) => setCallForm((current) => ({ ...current, status: value }))}
                      placeholder="Select status"
                      clearable={false}
                    />
                  </label>
                  <label className="form-field">
                    <span>Duration Seconds</span>
                    <input
                      type="number"
                      min="0"
                      value={callForm.duration_seconds ?? ''}
                      onChange={(event) => setCallForm((current) => ({ ...current, duration_seconds: event.target.value ? Number(event.target.value) : null }))}
                      placeholder="420"
                    />
                  </label>
                  <label className="form-field">
                    <span>Recording</span>
                    <SearchableSelect
                      options={RECORDING_OPTIONS}
                      value={callForm.recording_requested ? 'yes' : 'no'}
                      onChange={(value) => setCallForm((current) => ({ ...current, recording_requested: value === 'yes' }))}
                      placeholder="Recording"
                      clearable={false}
                    />
                  </label>
                </div>
                <label className="form-field">
                  <span>Summary</span>
                  <input
                    value={callForm.summary ?? ''}
                    onChange={(event) => setCallForm((current) => ({ ...current, summary: event.target.value }))}
                    placeholder="Discussed AP workflow and agreed to schedule a demo."
                  />
                </label>
                <label className="form-field">
                  <span>Notes</span>
                  <textarea
                    rows={3}
                    value={callForm.body ?? ''}
                    onChange={(event) => setCallForm((current) => ({ ...current, body: event.target.value }))}
                    placeholder="Capture objections, pain points, and next steps."
                  />
                </label>
                <div className="form-actions">
                  <button
                    type="button"
                    className="button button-primary"
                    onClick={() => void handleStartLiveCall()}
                    disabled={!callSession?.available || !callForm.to_number?.trim() || isStarting || Boolean(activeCall)}
                  >
                    <Phone size={16} />
                    {isStarting ? 'Connecting…' : activeCall ? 'Live Call Active' : 'Start Live Call'}
                  </button>
                  <button type="submit" className="button button-secondary" disabled={createCallMutation.isPending || !callForm.to_number?.trim()}>
                    <Phone size={16} />
                    {createCallMutation.isPending ? 'Saving Call…' : 'Save Call Record'}
                  </button>
                </div>
              </form>

              {calls?.items.length ? (
                <div className="timeline-list">
                  {calls.items.map((call) => (
                    <article key={call.id} className="timeline-item call-item">
                      <div className="timeline-meta">
                        <strong>{call.to_number}</strong>
                        <span>{formatRelativeDate(call.ended_at || call.created_at)}</span>
                      </div>
                      <div className="call-item-meta">
                        <span className="pill pill-neutral">{call.status.replace('_', ' ')}</span>
                        <span className="pill pill-soft">{call.recording_status}</span>
                        <span className="pill pill-soft">{call.transcript_status}</span>
                      </div>
                      <p>{call.duration_seconds ? `${call.duration_seconds}s` : 'No duration captured yet'}</p>
                    </article>
                  ))}
                </div>
              ) : (
                <p className="muted-copy">No calls logged for this lead yet.</p>
              )}
            </div>
          </SectionCard>

          <SectionCard title="AI Panel" subtitle="Scoring, enrichment, and AI draft context for this lead.">
            <AIPanel leadId={leadId} />
          </SectionCard>

          <SectionCard title="Convert Lead" subtitle="Turn a qualified lead into an account and optional opportunity.">
            <div className="form-grid">
              <label className="form-field">
                <span>Account Name</span>
                <input value={convertAccountName} onChange={(event) => setConvertAccountName(event.target.value)} />
              </label>
              <label className="form-field checkbox-field">
                <span>Create Opportunity</span>
                <input type="checkbox" checked={createOpportunity} onChange={(event) => setCreateOpportunity(event.target.checked)} />
              </label>
              <label className="form-field">
                <span>Opportunity Name</span>
                <input
                  value={opportunityName}
                  onChange={(event) => setOpportunityName(event.target.value)}
                  disabled={!createOpportunity}
                />
              </label>
            </div>

            <div className="form-actions">
              <button type="button" className="button button-secondary" onClick={handleConvertLead} disabled={convertLeadMutation.isPending}>
                <Sparkles size={16} />
                {convertLeadMutation.isPending ? 'Converting…' : 'Convert Lead'}
              </button>
            </div>

            {convertLeadMutation.data && !convertLeadMutation.data.account_id ? (
              <p className="success-copy">Converted lead using an existing account record.</p>
            ) : null}
          </SectionCard>

          <SectionCard title="Lead Timeline" subtitle="Recent founder notes and CRM activity.">
            {timeline?.items.length ? (
              <div className="timeline-list">
                {timeline.items.map((activity) => (
                  <article key={activity.id} className="timeline-item">
                    <div className="timeline-meta">
                      <strong>{activity.summary}</strong>
                      <span>{formatDate(activity.occurred_at)}</span>
                    </div>
                    <p>{activity.body || 'No additional note body.'}</p>
                  </article>
                ))}
              </div>
            ) : (
              <p className="muted-copy">No lead activity yet.</p>
            )}
          </SectionCard>

          <SectionCard title="Add Note" subtitle="Capture context for the next founder touchpoint.">
            <form className="detail-subsection" onSubmit={handleAddNote}>
              <label className="form-field">
                <span>Note</span>
                <textarea rows={3} value={noteBody} onChange={(event) => setNoteBody(event.target.value)} />
              </label>
              <div className="form-actions">
                <button type="submit" className="button button-secondary" disabled={addNoteMutation.isPending || !noteBody.trim()}>
                  {addNoteMutation.isPending ? 'Saving Note…' : 'Save Note'}
                </button>
              </div>
            </form>
          </SectionCard>
        </>
      ) : null}
    </div>
  );
}
